import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  effect,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { EventqDialogPopover } from '../dialog-popover/dialog-popover';
import { EventqMiniPlaybackControls } from '../mini-playback-controls/mini-playback-controls';

export type EventqMediaEnlargeVariant = 'snapshot' | 'clip';

/**
 * Centered enlarge media dialog (Figma Enlarge Snapshot / Enlarge Clip).
 */
@Component({
  selector: 'eventq-media-enlarge-popover',
  imports: [EventqDialogPopover, EventqMiniPlaybackControls, MatIcon],
  templateUrl: './media-enlarge-popover.html',
  styleUrl: './media-enlarge-popover.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventqMediaEnlargePopover {
  readonly open = input(false);
  readonly title = input.required<string>();
  readonly variant = input<EventqMediaEnlargeVariant>('snapshot');
  readonly imageSrc = input<string | undefined>(undefined);
  readonly videoSrc = input<string | undefined>(undefined);
  readonly posterSrc = input<string | undefined>(undefined);
  readonly panelWidth = input('960px');
  readonly deletable = input(true);

  readonly closed = output<void>();
  readonly delete = output<void>();

  private readonly videoRef = viewChild<ElementRef<HTMLVideoElement>>('videoEl');

  readonly playing = signal(false);
  readonly progress = signal(0);
  readonly elapsedLabel = signal('0:00');
  readonly durationLabel = signal('0:00');
  readonly playbackDisabled = signal(false);

  constructor() {
    effect(() => {
      const open = this.open();
      const variant = this.variant();
      const src = this.videoSrc();
      if (!open) {
        this.pauseVideo();
        this.playing.set(false);
        this.progress.set(0);
        this.elapsedLabel.set('0:00');
        return;
      }
      this.playbackDisabled.set(variant === 'clip' && !src);
      if (variant === 'clip' && src) {
        queueMicrotask(() => this.tryAutoplay());
      }
    });
  }

  onClose(): void {
    this.pauseVideo();
    this.closed.emit();
  }

  onDelete(event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    this.delete.emit();
  }

  onPlayToggle(): void {
    const video = this.videoRef()?.nativeElement;
    if (!video || this.playbackDisabled()) {
      return;
    }
    if (video.paused) {
      void video.play();
      this.playing.set(true);
    } else {
      video.pause();
      this.playing.set(false);
    }
  }

  onSeek(ratio: number): void {
    const video = this.videoRef()?.nativeElement;
    if (!video || !Number.isFinite(video.duration) || video.duration <= 0) {
      return;
    }
    video.currentTime = ratio * video.duration;
    this.syncFromVideo(video);
  }

  onTimeUpdate(): void {
    const video = this.videoRef()?.nativeElement;
    if (!video) {
      return;
    }
    this.syncFromVideo(video);
  }

  onLoadedMetadata(): void {
    const video = this.videoRef()?.nativeElement;
    if (!video) {
      return;
    }
    this.durationLabel.set(formatClock(video.duration));
    this.syncFromVideo(video);
    if (this.open() && this.variant() === 'clip') {
      this.tryAutoplay();
    }
  }

  onVideoEnded(): void {
    this.playing.set(false);
  }

  private pauseVideo(): void {
    const video = this.videoRef()?.nativeElement;
    if (video && !video.paused) {
      video.pause();
    }
  }

  private tryAutoplay(): void {
    const video = this.videoRef()?.nativeElement;
    if (!video || this.playbackDisabled() || !this.open() || this.variant() !== 'clip') {
      return;
    }
    const playResult = video.play();
    if (playResult && typeof playResult.then === 'function') {
      void playResult.then(
        () => this.playing.set(true),
        () => this.playing.set(false),
      );
      return;
    }
    this.playing.set(!video.paused);
  }

  private syncFromVideo(video: HTMLVideoElement): void {
    const duration = video.duration;
    if (!Number.isFinite(duration) || duration <= 0) {
      this.progress.set(0);
      this.elapsedLabel.set(formatClock(video.currentTime));
      return;
    }
    this.progress.set((video.currentTime / duration) * 100);
    this.elapsedLabel.set(formatClock(video.currentTime));
    this.durationLabel.set(formatClock(duration));
  }
}

function formatClock(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) {
    return '0:00';
  }
  const total = Math.floor(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}
